import React, { useState } from 'react';
import { toast } from 'sonner';
import { Plus, Search, Trash2, Mail, Phone, User, X, Pencil, Save, Upload, Info } from 'lucide-react';
import { BulkImportModal } from '../ui/BulkImportModal';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { cn } from '../../lib/utils';
import { supabase } from '../../lib/supabase';

export function FacultyList({ faculty, onUpdateFaculty, selectedFacultyId, focusedId, onSelect, readOnly, onRefresh }) {
  const [search, setSearch] = useState('');
  const [bulkImportOpen, setBulkImportOpen] = useState(false);

  const filtered = faculty.filter(f =>
    f.name.toLowerCase().includes(search.toLowerCase()) ||
    f.department.toLowerCase().includes(search.toLowerCase())
  );

  const openNew = () => {
    onSelect({ id: 'new', name: '', email: '', phone: '', department: '', specialization: '', max_hours_per_day: 6, seniority: 1 }, 'faculty');
  };

  return (
    <>
    <BulkImportModal
      open={bulkImportOpen}
      onOpenChange={setBulkImportOpen}
      entityName="faculty"
      onImportComplete={() => { onRefresh?.(); }}
    />
    <div className="flex flex-col h-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700/50 rounded-xl overflow-hidden shadow-sm">
      <div className="px-4 py-3 border-b border-zinc-200 dark:border-zinc-700/50">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-base font-bold text-zinc-800 dark:text-zinc-100 tracking-tight">Faculty</h2>
            <p className="text-[0.65rem] text-zinc-500 mt-0.5">{faculty.length} members</p>
          </div>
          {!readOnly && (
            <div className="flex items-center gap-1.5">
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs border-zinc-300 dark:border-zinc-600 text-zinc-600 dark:text-zinc-300"
                onClick={() => setBulkImportOpen(true)}
                title="Bulk Import"
              >
                <Upload className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white" onClick={openNew}>
                <Plus className="w-3.5 h-3.5 mr-1" /> Add
              </Button>
            </div>
          )}
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-zinc-400" />
          <Input className="pl-8 h-8 text-xs bg-zinc-50 dark:bg-zinc-800" placeholder="Search faculty..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>
      <ScrollArea className="flex-1 p-3">
        <div className="space-y-2">
          {filtered.map((fac) => {
            return (
              <div
                key={fac.id}
                className={cn(
                  "data-card !p-3 cursor-pointer transition-all",
                  selectedFacultyId === fac.id && "ring-2 ring-blue-500/30",
                  focusedId === fac.id && "ring-2 ring-violet-500/50 shadow-md"
                )}
                onClick={() => onSelect(fac, 'faculty')}
                data-card-index={`faculty-${fac.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-600/10 dark:bg-blue-400/10 flex items-center justify-center text-xs font-bold text-blue-700 dark:text-blue-300 shrink-0">
                    {(fac.name || '').split(' ').map(n => n[0]).join('').substring(0, 2) || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-semibold text-zinc-800 dark:text-zinc-100 truncate">{fac.name}</h4>
                      <Badge variant={fac.status === 'active' ? 'default' : 'secondary'} className={cn("text-[0.55rem] h-3.5", fac.status === 'active' ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-amber-100 text-amber-700 border-amber-200")}>
                        {fac.status}
                      </Badge>
                    </div>
                    <p className="text-[0.65rem] text-zinc-500 dark:text-zinc-400 truncate">{fac.department} {fac.specialization ? ` · ${fac.specialization}` : ''}</p>
                  </div>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && <p className="text-sm text-zinc-400 text-center py-8">No faculty found</p>}
        </div>
      </ScrollArea>
    </div>
    </>
  );
}

export function FacultyDetail({ facultyItem, onUpdateFaculty, onClose, readOnly }) {
  const [editMode, setEditMode] = useState(facultyItem?.id === 'new');
  const [editForm, setEditForm] = useState({});

  React.useEffect(() => {
    if (facultyItem?.id === 'new') {
      setEditMode(true);
      setEditForm({ name: '', email: '', phone: '', department: '', specialization: '', max_hours_per_day: 6, seniority: 1 });
    } else {
      setEditMode(false);
      setEditForm({});
    }
  }, [facultyItem?.id]);

  const startEdit = () => {
    setEditMode(true);
    setEditForm({ 
      name: facultyItem.name, 
      email: facultyItem.email, 
      phone: facultyItem.phone || '', 
      department: facultyItem.department || '', 
      specialization: facultyItem.specialization || '', 
      max_hours_per_day: facultyItem.max_hours_per_day || 6,
      seniority: facultyItem.seniority || 1
    });
  };

  const saveEdit = async () => {
    if (!editForm.name || !editForm.email) { toast.error('Name and email are required'); return; }
    try {
      const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
      const team_id = profile.team_id;
      if (facultyItem.id === 'new') {
        const { data, error } = await supabase
          .from('faculty')
          .insert({ ...editForm, team_id })
          .select()
          .single();
        if (error) throw error;
        onUpdateFaculty(prev => [...prev, data]);
        toast.success('Faculty added');
        onClose();
      } else {
        const { error } = await supabase
          .from('faculty')
          .update(editForm)
          .eq('id', facultyItem.id)
          .eq('team_id', team_id);
        if (error) throw error;
        onUpdateFaculty(prev => prev.map(f => f.id === facultyItem.id ? { ...f, ...editForm } : f));
        toast.success('Faculty updated');
        setEditMode(false);
      }
    } catch (err) { toast.error(err.message || 'Failed to save faculty'); }
  };

  const remove = async () => {
    try {
      const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
      const team_id = profile.team_id;
      const { error } = await supabase.from('faculty').delete().eq('id', facultyItem.id).eq('team_id', team_id);
      if (error) throw error;
      onUpdateFaculty(prev => prev.filter(f => f.id !== facultyItem.id));
      onClose();
      toast.success('Faculty removed');
    } catch (err) { toast.error(err.message || 'Failed to delete faculty'); }
  };

  if (!facultyItem) return null;

  return (
    <div className="flex flex-col h-full bg-white dark:bg-zinc-900 animate-slide-in-right">
      <div className="px-5 py-4 border-b border-zinc-200 dark:border-zinc-700/50 flex items-center justify-between">
        <h3 className="text-base font-semibold truncate">{facultyItem.id === 'new' ? 'Add Faculty' : 'Faculty Details'}</h3>
        <div className="flex items-center gap-1">
          {editMode ? (
            <>
              <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={saveEdit}>
                <Save className="w-3 h-3 mr-1" /> Save
              </Button>
              {facultyItem.id !== 'new' && (
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditMode(false)}>Cancel</Button>
              )}
            </>
          ) : (
            !readOnly && (
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={startEdit}>
                <Pencil className="w-3 h-3 mr-1" /> Edit
              </Button>
            )
          )}
          <button className="w-7 h-7 rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-800 flex items-center justify-center text-zinc-400" onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
      </div>
      <ScrollArea className="flex-1 p-5">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 rounded-full bg-blue-600/10 flex items-center justify-center text-lg font-bold text-blue-700 dark:text-blue-300">
            {((editForm.name || facultyItem.name || '').split(' ').map(n => n[0]).join('').substring(0, 2) || '?')}
          </div>
          <div className="flex-1">
            {editMode ? (
              <Input className="text-base font-bold h-8" value={editForm.name || ''} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} placeholder="Full Name" />
            ) : (
              <>
                <h3 className="text-base font-bold text-zinc-800 dark:text-zinc-100">{facultyItem.name}</h3>
                <p className="text-xs text-zinc-500">{facultyItem.department}</p>
              </>
            )}
          </div>
        </div>
        {editMode ? (
          <div className="space-y-3">
            <div><Label className="text-xs">Email</Label><Input className="mt-1" value={editForm.email || ''} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} placeholder="email@univ.edu" /></div>
            <div><Label className="text-xs">Phone</Label><Input className="mt-1" value={editForm.phone || ''} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} placeholder="+1234567890" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Department</Label><Input className="mt-1" value={editForm.department || ''} onChange={e => setEditForm(f => ({ ...f, department: e.target.value }))} placeholder="CS" /></div>
              <div><Label className="text-xs">Seniority (1-5)</Label><Input type="text" inputMode="numeric" pattern="[1-5]*" className="mt-1" value={editForm.seniority || 1} onChange={e => setEditForm(f => ({ ...f, seniority: Math.min(5, Math.max(1, parseInt(e.target.value) || 1)) }))} /></div>
            </div>
            <div><Label className="text-xs">Specialization</Label><Input className="mt-1" value={editForm.specialization || ''} onChange={e => setEditForm(f => ({ ...f, specialization: e.target.value }))} placeholder="e.g. AI" /></div>
            <div>
              <Label className="text-xs">Max Workload (Hours/Day)</Label>
              <div className="relative mt-1">
                <Input 
                  type="text" 
                  inputMode="numeric" 
                  pattern="[0-9]*" 
                  value={editForm.max_hours_per_day ?? 6} 
                  onChange={e => setEditForm(f => ({ ...f, max_hours_per_day: e.target.value === '' ? '' : (parseInt(e.target.value) || 0) }))} 
                  className="pr-10"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[0.6rem] text-zinc-400 font-medium pointer-events-none">hrs/day</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-sm p-3 bg-zinc-50 dark:bg-zinc-800/50 rounded-lg border border-zinc-200 dark:border-zinc-700/50"><Mail className="w-4 h-4 text-zinc-400" /><span className="text-zinc-600 dark:text-zinc-300">{facultyItem.email}</span></div>
            {facultyItem.phone && <div className="flex items-center gap-3 text-sm p-3 bg-zinc-50 dark:bg-zinc-800/50 rounded-lg border border-zinc-200 dark:border-zinc-700/50"><Phone className="w-4 h-4 text-zinc-400" /><span className="text-zinc-600 dark:text-zinc-300">{facultyItem.phone}</span></div>}
            <div className="grid grid-cols-2 gap-3">
               <div className="flex items-center gap-3 text-sm p-3 bg-zinc-50 dark:bg-zinc-800/50 rounded-lg border border-zinc-200 dark:border-zinc-700/50">
                  <Badge variant="outline" className="text-[0.6rem] h-4">Seniority: {facultyItem.seniority || 1}</Badge>
               </div>
               <div className="flex items-center gap-3 text-sm p-3 bg-zinc-50 dark:bg-zinc-800/50 rounded-lg border border-zinc-200 dark:border-zinc-700/50">
                  <Badge variant="outline" className="text-[0.6rem] h-4">{facultyItem.specialization || 'General'}</Badge>
               </div>
            </div>
          </div>
        )}
        <div className="mt-6 p-4 rounded-lg bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200 dark:border-zinc-700/50">
          <h4 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">Scheduling Constraints</h4>
          <p className="text-sm text-zinc-600 dark:text-zinc-300">Max workload: <span className="font-semibold text-blue-600 dark:text-blue-400">{editMode ? editForm.max_hours_per_day : facultyItem.max_hours_per_day} hours/day</span></p>
          <div className="p-2.5 bg-white dark:bg-zinc-900 rounded border border-zinc-200 dark:border-zinc-800 mt-3">
            <p className="text-[0.65rem] text-zinc-500 leading-relaxed italic flex gap-2">
              <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-500" />
              <span>
                <b>Optimization Note:</b> The scheduler prioritizes assignments based on <b>Seniority</b> and respects <b>Max Workload</b> to ensure balanced schedules. 
                Data is stored locally and used only for internal resource optimization.
              </span>
            </p>
          </div>
        </div>
        {!readOnly && facultyItem.id !== 'new' && (
          <Button variant="ghost" size="sm" className="mt-4 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={remove}>
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove Faculty
          </Button>
        )}
      </ScrollArea>
    </div>
  );
}
